// Shared Speediance Target-vs-Actual helpers — the TRENDS exercise-loads
// panel and the per-date coach-session comparison (session inspector + TRENDS
// by-muscle panel) aggregate actual_set rows through the same code.

import { C } from './design';
import { exerciseKeyFor } from './strength-session';
import type { ActualSetRow, CoachStrengthExercise, WorkoutRow } from './types';

// ---------- Aggregation ----------

export type ExerciseActualSet = {
  setIdx: number;
  reps: number | null;
  durationS: number | null;
  /** Modal weight across the set's reps (headline summary). */
  weightKg: number | null;
  /** Full per-rep weight sequence; null for legacy rows / manual entries. */
  weightsPerRep: number[] | null;
};

export type ExerciseActual = {
  date: string;
  /**
   * The working set — the heaviest set in the bucket, ties broken by
   * highest reps. This is the row that compares to the operator's stored
   * working weight (`exercise_weights.weight_kg`) and that the
   * progression rule (`hit all reps clean → +2.5 kg`) operates on.
   *
   * Earlier versions surfaced the per-bucket *mean* weight here. That's
   * meaningless for compound lifts that ramp warmup → working — the mean
   * sits between the warmup load and the working load and matches
   * nothing in particular. Top-set is what the operator's mental model
   * actually tracks.
   */
  topSet: ExerciseActualSet;
  /** Every set in the bucket, ordered by set_idx ascending, for drill-down. */
  sets: ExerciseActualSet[];
  setCount: number;
  /**
   * Target working weight as it was at session time — server-derived
   * from change_log. Differs from the current exercise_weights value
   * when the engine has bumped the target since. All historical
   * comparison surfaces (the session inspector, TRENDS loads panel)
   * should prefer this over current `exercise_weights` to
   * avoid the "just-completed session reads as below target" bug.
   * Null only when the operator never set a working weight for this
   * exercise.
   */
  sessionTimeTargetKg: number | null;
};

// Latest actual per exercise_key. Buckets sets per (exercise_key, date) and
// keeps the most-recent-date bucket per key. Returns the top set as the
// headline + the full per-set list for drill-down.
export function buildLatestActuals(rows: ActualSetRow[]): Map<string, ExerciseActual> {
  const byKey = new Map<string, ExerciseActual>();
  const perKeyPerDate = new Map<string, Map<string, ActualSetRow[]>>();
  for (const r of rows) {
    const inner = perKeyPerDate.get(r.exercise_key) ?? new Map();
    const bucket = inner.get(r.date) ?? [];
    bucket.push(r);
    inner.set(r.date, bucket);
    perKeyPerDate.set(r.exercise_key, inner);
  }
  for (const [key, inner] of perKeyPerDate) {
    const dates = [...inner.keys()].sort((a, b) => b.localeCompare(a));
    const latestDate = dates[0]!;
    const bucket = inner.get(latestDate)!;

    const sets: ExerciseActualSet[] = [...bucket]
      .sort((a, b) => a.set_idx - b.set_idx)
      .map((s) => ({
        setIdx: s.set_idx,
        reps: s.reps_done,
        durationS: s.duration_s,
        weightKg: s.weight_done_kg,
        weightsPerRep: s.weights_per_rep,
      }));

    // Top set = heaviest weight; ties broken by highest reps. Null weights
    // sort last (treated as 0 for comparison purposes — a logged set with
    // unknown weight shouldn't out-rank a set with a real weight).
    const topSet = pickTopSet(sets);

    // Per-row session-time target is uniform across a single session's
    // sets (they all share the same sync moment, so the server's
    // derivation returns the same number). Pull from the first row;
    // any row in the bucket would do.
    const sessionTimeTargetKg = bucket[0]!.target_kg_at_session ?? null;

    byKey.set(key, {
      date: latestDate,
      topSet,
      sets,
      setCount: sets.length,
      sessionTimeTargetKg,
    });
  }
  return byKey;
}

function pickTopSet(sets: ExerciseActualSet[]): ExerciseActualSet {
  // Caller guarantees sets.length >= 1 (only called from buildLatestActuals
  // inside a non-empty bucket loop). Default to the first as a typesafe
  // anchor; the loop replaces it as needed.
  let best = sets[0]!;
  for (const s of sets) {
    const sw = s.weightKg ?? -Infinity;
    const bw = best.weightKg ?? -Infinity;
    if (sw > bw) {
      best = s;
      continue;
    }
    if (sw === bw && (s.reps ?? -Infinity) > (best.reps ?? -Infinity)) {
      best = s;
    }
  }
  return best;
}

/**
 * Compact per-set chip label. For a uniform set this collapses to the
 * familiar "8×50.0", but when `weightsPerRep` shows the autopilot moved
 * load mid-set, the chip surfaces it as a range — e.g. "10 reps · 10→25"
 * — so the operator notices instead of seeing a single misleading number.
 *
 *   • uniform set (one weight)         → "8×50.0"
 *   • mixed set, all values rendered   → "10 reps · 10→25"
 *   • no per-rep data (legacy/manual)  → "8×50.0"  (same as uniform)
 *   • no reps                          → "—"
 */
export function formatSetChip(set: ExerciseActualSet): string {
  if (set.durationS !== null) {
    return set.weightKg != null && set.weightKg > 0
      ? `${set.durationS}s×${set.weightKg.toFixed(1)}`
      : `${set.durationS}s`;
  }
  if (set.reps === null) return '—';
  const repStr = set.reps.toFixed(0);
  // Mirror the server-side parser's discipline: only consider finite rep
  // weights when computing distinct values / range. The API write path
  // already filters out NaN/Infinity, but rendering shouldn't trust the
  // shape — a non-finite leaking through would render as "NaN→25" otherwise.
  const finite = set.weightsPerRep?.filter((n) => Number.isFinite(n)) ?? [];
  if (finite.length === 0) {
    return `${repStr}×${set.weightKg !== null ? set.weightKg.toFixed(1) : '—'}`;
  }
  const distinct = [...new Set(finite)];
  if (distinct.length === 1) {
    return `${repStr}×${distinct[0]!.toFixed(1)}`;
  }
  const lo = Math.min(...distinct);
  const hi = Math.max(...distinct);
  // For 2+ distinct weights, show the range so the autopilot drift is
  // visible at a glance. The order (lo → hi) matches the natural read,
  // not the in-set order — the chip's job is "here's how much it varied",
  // not "here's the exact sequence". The expanded view can show order.
  return `${repStr} reps · ${lo.toFixed(1)}→${hi.toFixed(1)}`;
}

/**
 * Compact per-set chip label for the *target* (prescription) side. No
 * per-rep variability concept on the target side — just `reps × weight`.
 * Mirrors the uniform-set output of `formatSetChip` so the target row
 * and actual row read in parallel.
 *
 *   • workingKg known → "8×25.0"
 *   • workingKg null  → "8×—"   (so the chip still renders, signalling
 *                                 "you haven't set the working weight yet")
 */
export function formatTargetSetChip(
  reps: number,
  weightKg: number | null,
  isUnilateral = false,
  /** Hold duration in seconds — set for TIMED sets (planks etc.); the chip
   *  then reads "50s" (or "50s×10.0" for a weighted hold) instead of the
   *  meaningless "1×0.0" a rep-encoding would produce. */
  durationS: number | null = null,
): string {
  const side = isUnilateral ? '/side' : '';
  if (durationS !== null) {
    const weighted =
      weightKg !== null && Number.isFinite(weightKg) && weightKg > 0
        ? `×${weightKg.toFixed(1)}`
        : '';
    return `${durationS}s${weighted}${side}`;
  }
  const w = weightKg !== null && Number.isFinite(weightKg) ? weightKg.toFixed(1) : '—';
  // Unilateral exercises (per-leg/side) append the marker so the chip
  // reads "8×20.0/side" — making the per-side rep intent explicit. The
  // prescription's exact /leg-vs-/side wording isn't stored on the spec,
  // so we use the generic /side.
  return `${reps}×${w}${side}`;
}

/** Most recent date any actual was recorded across `actuals` (the
 *  buildLatestActuals map). Null when nothing has synced yet. */
export function latestSyncedAt(actuals: Map<string, ExerciseActual>): string | null {
  let latest: string | null = null;
  for (const a of actuals.values()) {
    if (latest === null || a.date > latest) latest = a.date;
  }
  return latest;
}

// ---------- Divergence ----------

export function divergenceColor(
  actualReps: number | null,
  actualWeight: number | null,
  targetReps: number,
  targetWeight: number,
): string {
  if (actualReps === null || actualWeight === null) return C.text;
  const repsBelow = actualReps < targetReps;
  const weightBelow = actualWeight < targetWeight - 0.01;
  const repsExceeds = actualReps > targetReps;
  const weightExceeds = actualWeight > targetWeight + 0.01;
  if (repsBelow || weightBelow) return C.warn;
  if (repsExceeds && weightExceeds) return C.signal;
  return C.text;
}

// ---------- Coach session target-vs-actual (0027) ----------
//
// A coach prescription is per-date with its OWN exercise list + explicit
// per-set weights. This builds a per-DATE comparison: each APPLY-d coach
// strength session vs the actuals logged THAT day.
//
// Matching key: actuals are keyed by the exercise identity (`g:<group_id>`
// from the readback, the exercise's name for manual bodyweight actuals) — the
// same rule the APPLY mirror uses. `exerciseKeyFor` reproduces it here and we
// match (date, key) against the day's actuals.

/** One prescribed exercise paired with its actuals for the session date. */
export type CoachExerciseComparison = {
  name: string;
  /** Heaviest prescribed set weight (the headline target). */
  prescribedTopKg: number;
  /** Prescribed set count + modal/representative reps, for the target label. */
  prescribedSets: number;
  prescribedReps: number | null;
  prescribedSetDetails: CoachStrengthExercise['sets'];
  /** Actuals logged for this exercise on the session date. Null if none. */
  actual: ExerciseActual | null;
};

export type CoachSessionComparison = {
  date: string;
  title: string;
  exercises: CoachExerciseComparison[];
  /** True when at least one prescribed exercise has actuals logged that day. */
  hasAnyActuals: boolean;
  /**
   * True when EVERY prescribed set is weight_kg=0 (a machine-less bodyweight
   * session — no actual_set rows will ever exist). Mirrors the server's
   * by-muscle bodyweight gate. Bodyweight sessions are tracked by reps, not
   * load, so the renderer reads completion + derived reps rather than waiting
   * on absent machine actuals.
   */
  bodyweight: boolean;
  /** workout_log.status === 'completed' for the session date. */
  completed: boolean;
};

function prescribedTop(sets: { weight_kg: number }[]): number {
  return sets.reduce((max, s) => (s.weight_kg > max ? s.weight_kg : max), 0);
}

// The working-set reps = the LAST set's reps (matches the static convention
// where warmups ramp then the working sets carry the prescribed reps). NOT a
// statistical mode — named for what it returns.
function workingSetReps(sets: { reps?: number }[]): number | null {
  const reps = sets.map((s) => s.reps).filter((r): r is number => r != null);
  if (reps.length === 0) return null;
  return reps[reps.length - 1]!;
}

// A session is bodyweight when EVERY prescribed set (across all exercises) is
// weight_kg=0. Mirrors the server's by-muscle gate. An empty session can't be
// bodyweight (the caller already skips empty sessions, but guard anyway).
function isBodyweightSession(session: { exercises: { sets: { weight_kg: number }[] }[] }): boolean {
  let sawSet = false;
  for (const ex of session.exercises) {
    for (const s of ex.sets) {
      sawSet = true;
      if (s.weight_kg !== 0) return false;
    }
  }
  return sawSet;
}

/**
 * Build per-date coach-session comparisons from the workout rows (which carry
 * the inlined coach_strength_session) and the raw actual_set rows (with date).
 * One entry per date that has an APPLY-d coach strength session.
 *
 * Only sessions on or before `today` are included — a target-vs-ACTUAL surface
 * is meaningless for future sessions (no actuals can exist yet), and an applied
 * coach plan often spans a week ahead. Result is ascending by date, so the last
 * entry is the most recent (today's session when there is one) and the order
 * matches Today / This Week's chronological reading.
 */
export function buildCoachSessionComparisons(
  workouts: WorkoutRow[],
  actualRows: ActualSetRow[],
  today: string,
): CoachSessionComparison[] {
  // Group actual sets by (date, exercise_key) so we can match a coach session
  // to the actuals logged ON ITS DATE (not the global latest-per-key).
  const actualsByDateKey = new Map<string, ActualSetRow[]>();
  for (const r of actualRows) {
    const k = `${r.date}|${r.exercise_key}`;
    const bucket = actualsByDateKey.get(k) ?? [];
    bucket.push(r);
    actualsByDateKey.set(k, bucket);
  }

  const out: CoachSessionComparison[] = [];
  for (const w of workouts) {
    const session = w.coach_strength_session;
    if (!session || session.exercises.length === 0) continue;
    // Skip future-dated sessions — no actuals can exist for them yet.
    if (w.date > today) continue;
    // Bodyweight session: EVERY prescribed set across all exercises is
    // weight_kg=0. Completion confirms the session, while any manual actual
    // rows remain the only source for reps or time performed.
    const isBodyweight = isBodyweightSession(session);
    const completed = w.status === 'completed';
    const exercises: CoachExerciseComparison[] = session.exercises.map((ex) => {
      const dayRows = actualsByDateKey.get(`${w.date}|${exerciseKeyFor(ex)}`) ?? [];
      return {
        name: ex.name,
        prescribedTopKg: prescribedTop(ex.sets),
        prescribedSets: ex.sets.length,
        prescribedReps: workingSetReps(ex.sets),
        prescribedSetDetails: ex.sets,
        actual: dayRows.length > 0 ? buildActualFromRows(dayRows) : null,
      };
    });
    out.push({
      date: w.date,
      title: w.coach_prescription?.name ?? 'Coach strength session',
      exercises,
      hasAnyActuals: exercises.some((e) => e.actual != null),
      bodyweight: isBodyweight,
      completed,
    });
  }
  // Ascending by date (oldest first) so the order matches Today / This Week's
  // chronological reading and a given date's card lines up with what those
  // surfaces show for the same day.
  out.sort((a, b) => a.date.localeCompare(b.date));
  return out;
}

/** Build a single ExerciseActual from a (date, key) bucket of raw rows. */
function buildActualFromRows(rows: ActualSetRow[]): ExerciseActual {
  const sets: ExerciseActualSet[] = [...rows]
    .sort((a, b) => a.set_idx - b.set_idx)
    .map((s) => ({
      setIdx: s.set_idx,
      reps: s.reps_done,
      durationS: s.duration_s,
      weightKg: s.weight_done_kg,
      weightsPerRep: s.weights_per_rep,
    }));
  return {
    date: rows[0]!.date,
    topSet: pickTopSet(sets),
    sets,
    setCount: sets.length,
    sessionTimeTargetKg: rows[0]!.target_kg_at_session ?? null,
  };
}
